import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Trophy, ArrowRight, Zap, Medal } from "lucide-react";
import { api, statusLabel } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/10680031/pexels-photo-10680031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const tierStyles = {
  1: { ring: "border-[#D4AF37]", chip: "bg-[#D4AF37] text-[#0A0A0A]", label: "GOUD" },
  2: { ring: "border-[#A0A0A0]", chip: "bg-[#A0A0A0] text-white", label: "ZILVER" },
  3: { ring: "border-[#CD7F32]", chip: "bg-[#CD7F32] text-white", label: "BRONS" },
};

const statusDot = (s) =>
  s === "completed" ? "bg-[#16A34A]" : s === "live" ? "bg-[#D4AF37]" : "bg-[#A0A0A0]";

export default function Landing() {
  const [board, setBoard] = useState([]);
  const [tours, setTours] = useState([]);

  useEffect(() => {
    api.get("/leaderboard").then(({ data }) => setBoard(data));
    api.get("/tournaments").then(({ data }) => setTours(data));
  }, []);

  const top3 = board.slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E5E5]">
        <img src={HERO_IMG} alt="Dartbord" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-white/55 backdrop-blur-[2px]" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-24 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <img src="/anni-logo-square.png" alt="Anni's Place" className="w-12 h-12 object-contain" />
              <span className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#D4AF37] px-3 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase">
                <Zap className="w-3.5 h-3.5" /> Dart Toernooien 2026
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-[#0A0A0A] leading-[0.95]">
              Anni's Place{" "}
              <br />
              <span className="text-[#D4AF37]">Zomer Ranking 2026</span>
            </h1>
            <p className="mt-6 text-lg text-[#3a3a3a] max-w-xl leading-relaxed">
              Het complete ranking-overzicht van onze zomercompetitie. Poulefase, knock-outs, 180's,
              gemiddeldes en de strijd om de seizoenstitel — allemaal op één plek.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link
                to="/leaderboard"
                data-testid="hero-rankings-btn"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#D4AF37] px-7 py-3.5 font-semibold hover:bg-[#1A1A1A] transition-colors"
              >
                <Trophy className="w-4 h-4" /> Stand Ranking
              </Link>
              <Link
                to="/tournaments"
                data-testid="hero-tournaments-btn"
                className="inline-flex items-center gap-2 bg-transparent border border-[#0A0A0A] text-[#0A0A0A] px-7 py-3.5 font-semibold hover:bg-white transition-colors"
              >
                Toernooien <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Top 3 Bento */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 lg:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">
              Huidige Stand
            </p>
            <h2 className="font-heading text-3xl lg:text-4xl tracking-tight font-bold">Bovenaan de ranking</h2>
          </div>
          <Link to="/leaderboard" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold hover:text-[#A8882D]">
            Volledige stand <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {top3.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] p-12 text-center text-[#525252]">
            Nog geen resultaten ingevoerd. De stand verschijnt zodra toernooiresultaten zijn ingevuld.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {top3.map((p, i) => {
              const tier = tierStyles[p.rank] || tierStyles[3];
              return (
                <motion.div
                  key={p.player_id}
                  data-testid={`top-player-${p.rank}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.1 }}
                  className={`bg-white border-2 ${tier.ring} p-6 ${i === 0 ? "md:-translate-y-3" : ""}`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 ${tier.chip}`}>
                      <Medal className="w-3.5 h-3.5" /> {tier.label}
                    </span>
                    <span className="font-heading text-5xl font-black text-[#E5E5E5]">#{p.rank}</span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-4">{p.name}</h3>
                  <div className="grid grid-cols-3 gap-2 border-t border-[#E5E5E5] pt-4">
                    <Stat label="Punten" value={p.total_points} />
                    <Stat label="180" value={p.count_180} />
                    <Stat label="TOT. AVG" value={p.bavg} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>

      {/* Tournaments grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div className="flex items-center gap-3 mb-8">
          <Target className="w-6 h-6 text-[#D4AF37]" />
          <h2 className="font-heading text-3xl lg:text-4xl tracking-tight font-bold">De Toernooien</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tours.map((t) => {
            const isFinale = t.name === "Finale";
            return (
              <Link
                key={t.id}
                to={`/tournaments/${t.id}`}
                data-testid={`tournament-card-${t.number}`}
                className={`group bg-white border p-5 transition-colors ${isFinale ? "border-[#D4AF37]" : "border-[#E5E5E5] hover:border-[#D4AF37]"}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono text-xs text-[#525252]">{isFinale ? "★" : "RONDE"}</span>
                  <span className="font-heading text-3xl font-black text-[#0A0A0A] group-hover:text-[#D4AF37] transition-colors">
                    {isFinale ? "F" : String(t.number).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-heading font-semibold text-base leading-tight flex items-center gap-1.5">
                  {t.name}
                  {isFinale && <span className="text-[#D4AF37]">★</span>}
                </h3>
                {t.date && <p className="mt-1 font-mono text-xs text-[#525252]">{t.date}{t.time ? ` · ${t.time}` : ""}</p>}
                {t.is_doubles && <span className="mt-2 inline-block text-[9px] uppercase tracking-wide bg-[#D4AF37] text-[#0A0A0A] px-1.5 py-0.5 font-bold">Koppel</span>}
                <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#525252]">
                  <span className={`w-2 h-2 rounded-full ${statusDot(t.status)}`} />
                  {statusLabel(t.status)}
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div>
    <div className="font-mono text-lg font-bold text-[#0A0A0A]">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-[#525252]">{label}</div>
  </div>
);
